import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type PlanInfoFragment = { __typename?: 'Plan', activeStorageLimit?: number | null, archivedStorageLimit?: number | null, cost: number, defaultPlan: boolean, eligibility: Types.PlanEligibility, enterprise?: boolean | null, id: string, mountedStorageLimit?: number | null, mountedStorageTrialLimit?: number | null, name?: string | null, paymentMethod: Types.PlanPaymentMethod, period: Types.PlanPeriod, perUserStorageIncrement?: number | null, projectLimit?: number | null, tier: Types.PlanTier, title: string, userLimit?: number | null, userMax?: number | null, version?: number | null, workspaceLimit?: number | null, availableFeatures: { __typename?: 'AvailableFeatures', allowedDomains: boolean, commentAttachments?: boolean | null, customBrandedShares?: boolean | null, devices: boolean, disableShareComments?: boolean | null, disableShareDownloads?: boolean | null, disableSharePreviousVersions?: boolean | null, fourKPlayback: boolean, hdr: boolean, id: string, mountedStorage: boolean, mountedStorageOverprovisioningAllowed: boolean, passwordProtectedShares: boolean, privateProjects: boolean, reelPlayer: boolean, restrictedFolders?: boolean | null, secureSharing: boolean, sessionBasedWatermarking: boolean, shareLinkExpiration: boolean, staticWatermark?: boolean | null, storageConnect?: boolean | null, transcription?: boolean | null, unlimitedWatermarkTemplates?: boolean | null }, addOns?: Array<{ __typename?: 'PlanAddOn', id: string, modifier?: string | null }> | null };

export type DiscountPlanInfoFragment = { __typename?: 'Account', id: string, subscription: { __typename?: 'AccountSubscription', id: string, discountOfferExpiresAt?: string | null, plan?: { __typename?: 'Plan', id: string, tier: Types.PlanTier, discountPlan?: { __typename?: 'Plan', cost: number, eligibility: Types.PlanEligibility, enterprise?: boolean | null, id: string, name?: string | null, period: Types.PlanPeriod, tier: Types.PlanTier, title: string, userLimit?: number | null, version?: number | null } | null } | null, trial: { __typename?: 'SubscriptionTrial', isActive: boolean } } };

export type PausedPlanInfoFragment = { __typename?: 'Account', id: string, subscription: { __typename?: 'AccountSubscription', id: string, pauseOfferExpiresAt?: string | null, plan?: { __typename?: 'Plan', id: string, pauseOfferDurationMonths?: number | null, pausedPlan?: { __typename?: 'Plan', cost: number, eligibility: Types.PlanEligibility, enterprise?: boolean | null, id: string, name?: string | null, period: Types.PlanPeriod, tier: Types.PlanTier, title: string, userLimit?: number | null, version?: number | null } | null } | null } };

export type OriginalPlanInfoFragment = { __typename?: 'Account', id: string, subscription: { __typename?: 'AccountSubscription', id: string, originalPlan?: { __typename?: 'Plan', cost: number, eligibility: Types.PlanEligibility, enterprise?: boolean | null, id: string, name?: string | null, period: Types.PlanPeriod, tier: Types.PlanTier, title: string, userLimit?: number | null, version?: number | null } | null } };

export const PlanInfo = /*#__PURE__*/ gql`
    fragment PlanInfo on Plan {
  activeStorageLimit
  archivedStorageLimit
  availableFeatures {
    allowedDomains
    commentAttachments
    customBrandedShares
    devices
    disableShareComments
    disableShareDownloads
    disableSharePreviousVersions
    fourKPlayback
    hdr
    id
    mountedStorage
    mountedStorageOverprovisioningAllowed
    passwordProtectedShares
    privateProjects
    reelPlayer
    restrictedFolders
    secureSharing
    sessionBasedWatermarking
    shareLinkExpiration
    staticWatermark
    storageConnect
    transcription
    unlimitedWatermarkTemplates
  }
  addOns {
    id
    modifier
  }
  cost
  defaultPlan
  eligibility
  enterprise
  id
  mountedStorageLimit
  mountedStorageTrialLimit
  name
  paymentMethod
  period
  perUserStorageIncrement
  projectLimit
  tier
  title
  userLimit
  userMax
  version
  workspaceLimit
}
    ` as unknown as DocumentNode<PlanInfoFragment, unknown>;
export const DiscountPlanInfo = /*#__PURE__*/ gql`
    fragment DiscountPlanInfo on Account {
  id
  subscription {
    id
    discountOfferExpiresAt
    plan {
      id
      tier
      discountPlan {
        cost
        eligibility
        enterprise
        id
        name
        period
        tier
        title
        userLimit
        version
      }
    }
    trial {
      isActive
    }
  }
}
    ` as unknown as DocumentNode<DiscountPlanInfoFragment, unknown>;
export const PausedPlanInfo = /*#__PURE__*/ gql`
    fragment PausedPlanInfo on Account {
  id
  subscription {
    id
    pauseOfferExpiresAt
    plan {
      id
      pausedPlan {
        cost
        eligibility
        enterprise
        id
        name
        period
        tier
        title
        userLimit
        version
      }
      pauseOfferDurationMonths
    }
  }
}
    ` as unknown as DocumentNode<PausedPlanInfoFragment, unknown>;
export const OriginalPlanInfo = /*#__PURE__*/ gql`
    fragment OriginalPlanInfo on Account {
  id
  subscription {
    id
    originalPlan {
      cost
      eligibility
      enterprise
      id
      name
      period
      tier
      title
      userLimit
      version
    }
  }
}
    ` as unknown as DocumentNode<OriginalPlanInfoFragment, unknown>;

        

        
export const usePlanInfoFragment = (
  input: string | Omit<UseFragmentOptions<PlanInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Plan' } };
    return input;
  })();

  return useFragment<PlanInfoFragment>({
    fragment: PlanInfo,
    fragmentName: 'PlanInfo',
    ...options,
  });
};
  
        
        

        
export const useDiscountPlanInfoFragment = (
  input: string | Omit<UseFragmentOptions<DiscountPlanInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<DiscountPlanInfoFragment>({
    fragment: DiscountPlanInfo,
    fragmentName: 'DiscountPlanInfo',
    ...options,
  });
};
  
        
        

        
export const usePausedPlanInfoFragment = (
  input: string | Omit<UseFragmentOptions<PausedPlanInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<PausedPlanInfoFragment>({
    fragment: PausedPlanInfo,
    fragmentName: 'PausedPlanInfo',
    ...options,
  });
};
  
        
        

        
export const useOriginalPlanInfoFragment = (
  input: string | Omit<UseFragmentOptions<OriginalPlanInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<OriginalPlanInfoFragment>({
    fragment: OriginalPlanInfo,
    fragmentName: 'OriginalPlanInfo',
    ...options,
  });
};
  
        